/**
 * Filtering and sorting for the puzzle archive.
 *
 * Kept out of the component so the rules are testable on their own — the
 * ordering in particular has edge cases (unrated puzzles, ties) that are much
 * easier to pin down here than through the DOM.
 */

import { MAX_DIFFICULTY, type Puzzle } from "./puzzlesApi";

export const SORT_OPTIONS = [
  { id: "id", label: "Archive order" },
  { id: "difficulty", label: "Hardest first" },
  { id: "easiest", label: "Easiest first" },
  { id: "quality", label: "Best rated" },
  { id: "rated", label: "Most rated" },
  { id: "newest", label: "Newest" },
] as const;

export type SortId = (typeof SORT_OPTIONS)[number]["id"];

export interface PuzzleFilters {
  readonly search: string;
  readonly set: string | null;
  readonly sort: SortId;
  /** Hide anything the signed-in member has already rated. */
  readonly unratedOnly: boolean;
}

export const DEFAULT_FILTERS: PuzzleFilters = {
  search: "",
  set: null,
  sort: "id",
  unratedOnly: false,
};

/**
 * The difficulty to *sort* by: the club's if anyone has voted, otherwise the
 * creator's original number, brought onto the club's scale first.
 *
 * This is an ordering key, not a display value. Both numbers are always shown
 * on the card — one never stands in for the other there. The fallback exists so
 * that "hardest first" is still useful before many ratings have accumulated,
 * rather than filing 140 unrated puzzles under "unknown".
 *
 * The clamp is what makes the two comparable. The sheet's column is, in its
 * author's words, "a 1-10+ scale", and it means the "+": 17 of the 140 archived
 * puzzles carry a number above 10, up to 20. Ranking those raw put every one of
 * them above a puzzle the club had actually voted a 10.0 — the highest score a
 * member can give — so no amount of rating could ever move a real answer to the
 * top of the list. A creator's 12, 15 or 20 all say the same thing the club
 * scale can express, which is "at least as hard as our maximum"; ordering
 * *within* those is a distinction the club scale does not have, so ties fall to
 * the list's stable tiebreak.
 */
export function effectiveDifficulty(puzzle: Puzzle): number | null {
  if (puzzle.ratings.difficulty !== null) return puzzle.ratings.difficulty;
  if (puzzle.archive_difficulty === null) return null;
  return Math.min(puzzle.archive_difficulty, MAX_DIFFICULTY);
}

/**
 * Free-text match over the fields the search box advertises, including the
 * `#id` affordance in its placeholder.
 *
 * Structural rather than `Puzzle`-typed so the admin archive shares it: the two
 * screens search the same collection, and when they drifted an officer could
 * fail to find a puzzle the public page lists and file a duplicate.
 */
export function matchesSearch(
  puzzle: {
    readonly id: number;
    readonly title: string;
    readonly creator: string | null;
    readonly set_name: string | null;
  },
  needle: string,
): boolean {
  if (!needle) return true;
  const haystack = [
    puzzle.title,
    puzzle.creator,
    puzzle.set_name,
    `#${puzzle.id}`,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(needle);
}

/**
 * Sort comparators. Every one puts puzzles with no value last rather than
 * treating a missing rating as a zero — an unrated puzzle is unknown, not bad.
 */
/**
 * Whether this puzzle's difficulty is a measurement rather than a guess.
 *
 * Used to break ties between the two scales: a clamped archive number means "at
 * least as hard as our maximum", a club average means "this hard". When they
 * land on the same value the measured one is the better answer, and leaving it
 * to the id tiebreak would order them by nothing at all.
 */
const isClubRated = (puzzle: Puzzle): boolean =>
  puzzle.ratings.difficulty !== null;

const COMPARATORS: Record<SortId, (a: Puzzle, b: Puzzle) => number> = {
  id: (a, b) => a.id - b.id,
  difficulty: byValue(effectiveDifficulty, "desc", isClubRated),
  easiest: byValue(effectiveDifficulty, "asc", isClubRated),
  quality: byValue((puzzle) => puzzle.ratings.quality, "desc"),
  rated: (a, b) => b.ratings.count - a.ratings.count || a.id - b.id,
  newest: (a, b) =>
    (b.added_on ?? "").localeCompare(a.added_on ?? "") || b.id - a.id,
};

/**
 * Order by a value that may be missing, always sinking the missing ones.
 *
 * The null check has to happen against the puzzles themselves rather than
 * against pre-swapped arguments: flipping the operands to reverse the order
 * would flip where the nulls land too, floating every unrated puzzle to the
 * top of "hardest first". An unrated puzzle is unknown, not extreme.
 */
function byValue(
  read: (puzzle: Puzzle) => number | null,
  direction: "asc" | "desc",
  prefer?: (puzzle: Puzzle) => boolean,
): (a: Puzzle, b: Puzzle) => number {
  return (a, b) => {
    const first = read(a);
    const second = read(b);
    if (first === null && second === null) return a.id - b.id;
    if (first === null) return 1;
    if (second === null) return -1;
    const gap = direction === "asc" ? first - second : second - first;
    if (gap !== 0) return gap;
    if (prefer && prefer(a) !== prefer(b)) return prefer(a) ? -1 : 1;
    // A stable tiebreak, so the list does not shuffle between renders.
    return a.id - b.id;
  };
}

export function applyFilters(
  puzzles: readonly Puzzle[],
  filters: PuzzleFilters,
): readonly Puzzle[] {
  const needle = filters.search.trim().toLowerCase();

  const kept = puzzles.filter((puzzle) => {
    if (filters.set && puzzle.set_name !== filters.set) return false;
    if (filters.unratedOnly && puzzle.my_rating) return false;
    return matchesSearch(puzzle, needle);
  });

  // A copy: sorting the array the caller handed us would mutate their state.
  return [...kept].sort(COMPARATORS[filters.sort]);
}
